/**
 * ArticleImagesPanel — upload / reorder / caption up to 6 images per
 * article. Matches the Oct 8 2026 addendum (P1):
 *   • 6 images max, each ≤ 20 MB
 *   • JPEG, PNG, WebP, HEIC accepted (HEIC → WebP server-side)
 *   • Drag-and-drop + accessible ↑/↓ buttons so mobile + keyboard
 *     users can reorder without a drag gesture
 *   • Alt / Caption / Credit per image
 *
 * Needs an existing article id. For brand-new drafts we render a hint
 * telling the author to save first, since the upload endpoint is keyed
 * by article.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowUp, ArrowDown, Trash2, Upload, Loader2, GripVertical, X,
  Image as ImageIcon, Check, AlertCircle,
} from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;
const MAX_IMAGES = 6;
const MAX_BYTES  = 20 * 1024 * 1024;
const ACCEPTED   = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];
// Browsers often send `image/heic` with an empty type for .heic — fall
// back to the extension when the browser lies.
const ACCEPTED_EXTS = /\.(jpg|jpeg|png|webp|heic|heif)$/i;


function getToken() {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem("sp_token") || window.localStorage.getItem("token");
}


export default function ArticleImagesPanel({ articleId, onCoverPick }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [error, setError] = useState("");
  const [draggingId, setDraggingId] = useState(null);
  const fileRef = useRef(null);

  // ─── Load ────────────────────────────────────────────────────
  const refresh = useCallback(async () => {
    if (!articleId) { setImages([]); return; }
    setLoading(true);
    try {
      const token = getToken();
      const r = await fetch(`${BASE}/articles/${articleId}/images`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error(`${r.status}`);
      const j = await r.json();
      setImages(j.images || []);
    } catch (e) {
      setError(`Could not load images: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [articleId]);

  useEffect(() => { refresh(); }, [refresh]);

  // ─── Upload ──────────────────────────────────────────────────
  const uploadFiles = useCallback(async (files) => {
    if (!articleId) {
      setError("Save the draft first — images attach to a specific article.");
      return;
    }
    const list = Array.from(files || []);
    if (!list.length) return;
    const slotsLeft = MAX_IMAGES - images.length;
    if (slotsLeft <= 0) {
      setError(`You already have ${MAX_IMAGES} images on this article.`);
      return;
    }
    const toSend = list.slice(0, slotsLeft);
    if (list.length > slotsLeft) {
      setError(`Only uploading ${slotsLeft} of ${list.length} — ${MAX_IMAGES}-image limit per article.`);
    } else {
      setError("");
    }

    setUploadingCount(toSend.length);
    const token = getToken();
    try {
      for (const f of toSend) {
        // Client-side early reject so a 20 MB+ upload doesn't start.
        if (f.size > MAX_BYTES) {
          setError(`${f.name}: too large (${Math.round(f.size / 1024 / 1024)} MB, max 20 MB).`);
          continue;
        }
        const typeOk = ACCEPTED.includes((f.type || "").toLowerCase()) ||
                       ACCEPTED_EXTS.test(f.name || "");
        if (!typeOk) {
          setError(`${f.name}: unsupported type. JPEG, PNG, WebP or HEIC.`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", f, f.name || "photo");
        const r = await fetch(`${BASE}/articles/${articleId}/images`, {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: fd,
        });
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          setError(j.detail || `Upload failed (${r.status})`);
          break;
        }
      }
      await refresh();
    } finally {
      setUploadingCount(0);
    }
  }, [articleId, images.length, refresh]);

  const onPickFiles = (e) => {
    uploadFiles(e.target.files);
    e.target.value = "";    // Allow re-selecting the same file.
  };

  const onDrop = (e) => {
    e.preventDefault();
    uploadFiles(e.dataTransfer.files);
  };

  // ─── Reorder ────────────────────────────────────────────────
  const move = async (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= images.length || fromIdx === toIdx) return;
    const next = images.slice();
    const [row] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, row);
    setImages(next);
    const token = getToken();
    try {
      await fetch(`${BASE}/articles/${articleId}/images/reorder`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ order: next.map((x) => x.id) }),
      });
    } catch (e) {
      // On failure, re-pull server state so the UI never diverges.
      await refresh();
    }
  };

  // ─── Mutate alt/caption/credit ──────────────────────────────
  const patchMeta = async (imgId, patch) => {
    // Optimistic local update.
    setImages((xs) => xs.map((x) => (x.id === imgId ? { ...x, ...patch } : x)));
    const token = getToken();
    try {
      await fetch(`${BASE}/articles/${articleId}/images/${imgId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(patch),
      });
    } catch { /* best-effort */ }
  };

  const remove = async (imgId) => {
    const token = getToken();
    const prev = images;
    setImages((xs) => xs.filter((x) => x.id !== imgId));
    try {
      const r = await fetch(`${BASE}/articles/${articleId}/images/${imgId}`, {
        method: "DELETE",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error(`${r.status}`);
    } catch {
      setImages(prev);     // rollback
      setError("Could not delete that image.");
    }
  };

  // ─── Render ─────────────────────────────────────────────────
  const atLimit = images.length >= MAX_IMAGES;
  const disabled = !articleId || atLimit || uploadingCount > 0;

  return (
    <section
      className="bg-white border border-slate-200 rounded-2xl p-4"
      data-testid="publisher-write-images"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1">
          <ImageIcon className="w-3 h-3" /> Images
        </div>
        <div
          className={`text-[11px] font-semibold ${atLimit ? "text-amber-600" : "text-slate-400"}`}
          data-testid="publisher-write-images-counter"
        >
          {images.length} / {MAX_IMAGES}
        </div>
      </div>

      {!articleId && (
        <div className="text-xs text-slate-500 bg-slate-50 border border-dashed border-slate-200 rounded-lg p-3">
          Save the draft first to attach images.
        </div>
      )}

      {articleId && (
        <>
          {/* Dropzone / picker */}
          <label
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDrop={onDrop}
            className={`block cursor-pointer rounded-xl border-2 border-dashed p-4 text-center transition-colors
              ${disabled ? "border-slate-200 bg-slate-50 cursor-not-allowed opacity-70"
                        : "border-slate-300 hover:border-blue-400 hover:bg-blue-50/40"}`}
            data-testid="publisher-write-images-dropzone"
          >
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
              multiple
              className="hidden"
              onChange={onPickFiles}
              disabled={disabled}
              data-testid="publisher-write-images-input"
            />
            <div className="flex flex-col items-center gap-1">
              {uploadingCount > 0 ? (
                <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
              ) : (
                <Upload className="w-5 h-5 text-slate-500" />
              )}
              <div className="text-xs text-slate-600 font-semibold">
                {uploadingCount > 0
                  ? `Uploading ${uploadingCount}…`
                  : (atLimit ? "6-image limit reached" : "Drop photos or click to upload")}
              </div>
              <div className="text-[10px] text-slate-400">
                JPEG · PNG · WebP · HEIC · up to 20 MB each
              </div>
            </div>
          </label>

          {error && (
            <div
              className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 flex items-start gap-1"
              data-testid="publisher-write-images-error"
            >
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{error}</span>
              <button onClick={() => setError("")} className="ml-auto text-amber-500 hover:text-amber-700">
                <X className="w-3 h-3" />
              </button>
            </div>
          )}

          {/* Rows */}
          <div className="mt-3 space-y-2">
            {loading && (
              <div className="text-xs text-slate-500 flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading…
              </div>
            )}
            {!loading && images.length === 0 && (
              <div className="text-xs text-slate-400">No images yet.</div>
            )}
            {images.map((img, i) => (
              <ImageRow
                key={img.id}
                img={img}
                index={i}
                total={images.length}
                onMoveUp={() => move(i, i - 1)}
                onMoveDown={() => move(i, i + 1)}
                onDragStart={() => setDraggingId(img.id)}
                onDragOver={(e) => { e.preventDefault(); }}
                onDrop={() => {
                  if (!draggingId || draggingId === img.id) return;
                  const from = images.findIndex((x) => x.id === draggingId);
                  const to = i;
                  setDraggingId(null);
                  move(from, to);
                }}
                onPatch={(patch) => patchMeta(img.id, patch)}
                onDelete={() => remove(img.id)}
                onCoverPick={onCoverPick}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}


function ImageRow({
  img, index, total,
  onMoveUp, onMoveDown,
  onDragStart, onDragOver, onDrop,
  onPatch, onDelete, onCoverPick,
}) {
  const [open, setOpen] = useState(false);
  const abs = `${process.env.REACT_APP_BACKEND_URL || ""}${img.url}`;
  return (
    <div
      className="flex gap-2 items-start bg-slate-50 border border-slate-200 rounded-lg p-2"
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      data-testid={`publisher-write-image-row-${index}`}
    >
      <div className="flex flex-col items-center gap-1 pt-1">
        <GripVertical className="w-3 h-3 text-slate-400 cursor-grab active:cursor-grabbing" />
        <button
          onClick={onMoveUp}
          disabled={index === 0}
          className="w-6 h-6 rounded-md hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-slate-600"
          aria-label="Move image up"
          title="Move up"
          data-testid={`publisher-write-image-up-${index}`}
        >
          <ArrowUp className="w-3 h-3" />
        </button>
        <button
          onClick={onMoveDown}
          disabled={index === total - 1}
          className="w-6 h-6 rounded-md hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-slate-600"
          aria-label="Move image down"
          title="Move down"
          data-testid={`publisher-write-image-down-${index}`}
        >
          <ArrowDown className="w-3 h-3" />
        </button>
      </div>

      <img
        src={abs}
        alt={img.alt || ""}
        loading="lazy"
        className="w-20 h-20 object-cover rounded-md border border-slate-200 shrink-0"
      />

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-1">
          <div className="text-[11px] text-slate-500 truncate">
            {img.width}×{img.height} · {Math.round((img.size || 0) / 1024)} KB
            {img.source_format === "heic" && (
              <span className="ml-1 text-[10px] font-bold text-emerald-600">HEIC→WEBP</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {onCoverPick && (
              <button
                onClick={() => onCoverPick(abs)}
                className="text-[10px] font-semibold text-blue-600 hover:text-blue-800 px-1 py-0.5 rounded hover:bg-white"
                title="Use as cover image"
                data-testid={`publisher-write-image-set-cover-${index}`}
              >
                Set cover
              </button>
            )}
            <button
              onClick={() => setOpen((x) => !x)}
              className="text-[10px] font-semibold text-slate-500 hover:text-slate-800 px-1 py-0.5 rounded hover:bg-white"
            >
              {open ? "Hide" : "Details"}
            </button>
            <button
              onClick={onDelete}
              className="w-6 h-6 rounded-md hover:bg-red-50 flex items-center justify-center text-red-500"
              aria-label="Delete image"
              title="Delete"
              data-testid={`publisher-write-image-delete-${index}`}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        </div>

        {open && (
          <div className="mt-2 space-y-1">
            <input
              type="text"
              placeholder="Alt text (describe the image for screen readers)"
              value={img.alt || ""}
              onChange={(e) => onPatch({ alt: e.target.value })}
              className="w-full text-xs border border-slate-200 rounded-md px-2 py-1 outline-none focus:border-blue-400"
              data-testid={`publisher-write-image-alt-${index}`}
            />
            <input
              type="text"
              placeholder="Caption (optional)"
              value={img.caption || ""}
              onChange={(e) => onPatch({ caption: e.target.value })}
              className="w-full text-xs border border-slate-200 rounded-md px-2 py-1 outline-none focus:border-blue-400"
              data-testid={`publisher-write-image-caption-${index}`}
            />
            <input
              type="text"
              placeholder="Credit (photographer, source)"
              value={img.credit || ""}
              onChange={(e) => onPatch({ credit: e.target.value })}
              className="w-full text-xs border border-slate-200 rounded-md px-2 py-1 outline-none focus:border-blue-400"
              data-testid={`publisher-write-image-credit-${index}`}
            />
            {!img.alt && (
              <div className="text-[10px] text-amber-700 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Alt text helps accessibility & SEO.
              </div>
            )}
            {img.alt && (
              <div className="text-[10px] text-emerald-700 flex items-center gap-1">
                <Check className="w-3 h-3" /> Alt text set.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
